import { Suspense, useEffect } from 'react';
import { Route, Routes, useParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import posthog from 'posthog-js';
import * as urls from 'routes/config';
import ProtectedStage from '@/components/AppGuards/ProtectedStage';
import Spinner from '@/components/Common/Spinner';
import OnboardingSecureAuth from '@/pages/Agency/OnboardingForms/AgentReadinessForm/components/OnboardingSecureAuth';
import OnboardingThankYou from '@/pages/Agency/OnboardingForms/AgentReadinessForm/components/OnboardingThankYou';
import AgentReadinessForm from '@/pages/Agency/OnboardingForms/AgentReadinessForm';
import StartAgentReadinessForm from '@/pages/Agency/OnboardingForms/StartAgentReadinessForm';
import { useGetPublicOnboardingForm, useLoginOnboardingForm } from '@/apis/agency/businessOnboarding/hooks';

const { base: ONBOARDING_BASE, stage1, stage2 } = urls.businessOnboarding;

function onboardingPathForStage(stage: string | undefined) {
  return stage === '2' ? `${ONBOARDING_BASE}/${stage2()}` : `${ONBOARDING_BASE}/${stage1()}`;
}

function OnboardingRedirectHandler() {
  const { formId, token } = useParams();
  const { data: onboardingData, isLoading: isFetchingForm } = useGetPublicOnboardingForm(formId!, {
    enabled: !!formId
  });
  const { mutate: loginOnboardingForm, isPending: isLoggingIn } = useLoginOnboardingForm();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      loginOnboardingForm(
        { token, update_message: '' },
        {
          onSuccess: (response) => {
            if (response?.token) {
              Cookies.set('onboard-token', response.form_token || '');
              Cookies.set('auth_token', response.token);
              Cookies.set('onboarding_id', response?.id?.toString() || '');
            }
            const stage = response.onboarding_config?.stage?.toString();
            navigate(onboardingPathForStage(stage), { replace: true });
          }
        }
      );
      return;
    }

    if (onboardingData && formId) {
      Cookies.set('onboard-token', formId);
      const stage = onboardingData.onboarding_config?.stage?.toString();
      navigate(onboardingPathForStage(stage), { replace: true });
    }
  }, [onboardingData, formId, token, navigate, loginOnboardingForm]);

  if (isFetchingForm || isLoggingIn) {
    return <Spinner />;
  }
  return null;
}

export default function BusinessOnboardingRoutes() {
  const { pathname } = useLocation();

  useEffect(() => {
    const isProduction = import.meta.env.VITE_APP_ENV === 'production';
    const isOnboardingStageRoute =
      pathname === `${ONBOARDING_BASE}/${stage1()}` || pathname === `${ONBOARDING_BASE}/${stage2()}`;

    if (!isProduction || !isOnboardingStageRoute || posthog.__loaded) {
      return;
    }

    posthog.init('phc_N9oEyAeaOb1Z69ScunkIA5OyQKEamYzTZ5xZqyygwkJ', {
      api_host: 'https://us.i.posthog.com',
      defaults: '2026-01-30'
    });
  }, [pathname]);

  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path='/' element={<Navigate to={urls.businessOnboarding.stage1()} replace />} />
        <Route path='/:formId' element={<OnboardingRedirectHandler />} />
        <Route path='/stage-1/:token' element={<OnboardingRedirectHandler />} />
        <Route path='/stage-2/:token' element={<OnboardingRedirectHandler />} />
        <Route path={urls.businessOnboarding.login} element={<OnboardingSecureAuth />} />
        <Route path={urls.businessOnboarding.stage1()} element={<StartAgentReadinessForm />} />
        <Route
          path={urls.businessOnboarding.stage2()}
          element={
            <ProtectedStage>
              <AgentReadinessForm />
            </ProtectedStage>
          }
        />
        <Route path={urls.businessOnboarding.submitted} element={<OnboardingThankYou />} />
      </Routes>
    </Suspense>
  );
}
